/**
 * components/riskTerminal/groupLine.js
 *
 * upstox-085 — the group line never wraps. The Claude Design handoff (positions-wide-handoff-2026-11-04, the
 * group-row block) gives it three STEPS, chosen by MEASURING the longest group line against the Structure
 * column (not by breakpoints); every group takes the same step:
 *
 *   step 1  everything inline, as before: the grade state in words, the flags in full.
 *   step 2  a grade state shared by EVERY group ("NOT GRADED · MARKET CLOSED") moves to the POSITIONS heading
 *           (a group that differs keeps its own words); flags use their short form.
 *   step 3  the amber flags and the grey words fold into a "+N" box (the hover lists them all).
 *
 * RED NEVER FOLDS — a red tag shortens ("AGAINST CONF."). If anything red ever had to be hidden the +N box
 * would be solid red with white text (foldTone); today nothing red is ever folded, so that box never shows.
 * An amber GRADE ("AGAINST FADING TREND") is a grade, not a flag: it shortens too and stays on the line.
 * The name is never cut. Rows are 38 px.
 */

import { useLayoutEffect, useRef, useState } from 'react';

/** Each flag carries its short form in its own definition. */
export const FLAG_DEFS = {
  regrouped_against_trend: { full: 'REGROUPED TODAY WHILE AGAINST TREND', short: 'REGROUPED VS TREND' },
  reading_switched_against_trend: { full: 'READING SWITCHED WHILE AGAINST TREND', short: 'READING SWITCHED' },
};

/** The two grade tags that are not "not graded": red (confirmed) and amber (fading), with their short forms. */
export const GRADE_WORDS = {
  confirmed: { full: 'AGAINST CONFIRMED TREND', short: 'AGAINST CONF.', tone: 'red' },
  fading: { full: 'AGAINST FADING TREND', short: 'AGAINST FADING', tone: 'amber' },
};

export const STEPS = [1, 2, 3];

/** The flag keys a structure carries today, from the server's marks. */
export function flagKeys(s) {
  const keys = [];
  if (s && s.regroup_mark_today) keys.push('regrouped_against_trend');
  if (s && s.reading_switched_today) keys.push('reading_switched_against_trend');
  return keys;
}

/**
 * The grade state every group shares, or null. Only a grey "not graded" state is hoisted to the heading: a red or
 * amber grade is per-group information and stays on its group. `groups` = [{ kind, gradeLabel }].
 */
export function sharedGradeState(groups) {
  if (!groups || !groups.length) return null;
  const first = groups[0];
  if (first.kind !== 'ungraded' || !first.gradeLabel) return null;
  return groups.every(g => g.kind === 'ungraded' && g.gradeLabel === first.gradeLabel) ? first.gradeLabel : null;
}

/**
 * What one group's line shows at a step.
 *   g = { kind, gradeLabel, flags: [keys], note }   (gradeLabel = the full grade words; note = "3 legs" when collapsed)
 * → { grade: {text, tone, kind} | null, flags: [{key, text}], note, fold: [{text, tone}], foldTone }
 */
export function groupLineParts(g, step, shared) {
  const gw = GRADE_WORDS[g.kind];
  const flagsAll = (g.flags || []).filter(k => FLAG_DEFS[k]);
  const hoisted = step >= 2 && g.kind === 'ungraded' && !!shared && g.gradeLabel === shared;
  let grade = null;
  if (gw) grade = { text: step >= 2 ? gw.short : gw.full, full: gw.full, tone: gw.tone, kind: g.kind };
  else if (g.kind === 'ungraded' && g.gradeLabel && !hoisted) grade = { text: g.gradeLabel, full: g.gradeLabel, tone: 'grey', kind: g.kind };
  let flags = flagsAll.map(k => ({ key: k, text: step >= 2 ? FLAG_DEFS[k].short : FLAG_DEFS[k].full, full: FLAG_DEFS[k].full, tone: 'amber' }));
  let note = g.note || false;
  const fold = [];
  if (step >= 3) {
    flags.forEach(f => fold.push({ text: f.full, tone: 'amber' }));
    flags = [];
    if (grade && grade.tone === 'grey') { fold.push({ text: grade.full, tone: 'grey' }); grade = null; }
    if (note) { fold.push({ text: note, tone: 'grey' }); note = false; }
  }
  return { grade, flags, note, fold, foldTone: fold.some(f => f.tone === 'red') ? 'red' : 'amber', hoisted };
}

/** True when a line's content is wider than its box (the one measurement the step is chosen by). */
export const overflows = node => !!node && node.scrollWidth > node.clientWidth + 1;

/**
 * The step for the positions track: starts at 1 and goes up while ANY group line overflows its Structure column.
 * It starts again from 1 whenever the track's width or the lines' content changes (`sig`), and the layout effect
 * runs before paint, so the user never sees the in-between steps. `el` is the track element (held in state).
 */
export function useGroupStep(el, sig) {
  const [step, setStep] = useState(1);
  const [width, setWidth] = useState(0);
  const last = useRef({ sig, width: 0 });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const read = () => setWidth(el.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  useLayoutEffect(() => {
    if (!el) return;
    if (last.current.sig !== sig || last.current.width !== width) {
      last.current = { sig, width };
      if (step !== 1) { setStep(1); return; }
    }
    if (step >= 3) return;
    const lines = el.querySelectorAll('[data-groupline]');
    for (let i = 0; i < lines.length; i += 1) {
      if (overflows(lines[i])) { setStep(step + 1); return; }
    }
  }, [el, sig, width, step]);
  return step;
}
